"use client";

import { CMSData } from "@/cms/models";
import { useRouter } from "next/navigation";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-light-svg-icons";
import { twMerge } from "tailwind-merge";
import RippleCtaButton from "./RippleCtaButton";

interface SearchBarComponentProps {
  config?: {
    cmsData: CMSData;
    bgColor?: string;
    input?: {
      placeholderText?: string;
      placeholderTextColor?: string;
      bgColor?: string;
      textColor?: string;
      borderColor?: string;
      focusBorderColor?: string;
    };
    button?: {
      text?: string;
      bgColor?: string;
      textColor?: string;
      hoverBgColor?: string;
      hoverTextColor?: string;
    };
  };
  toggleSearch?: () => void;
}

const SearchBarComponent: React.FC<SearchBarComponentProps> = ({
  config,
  toggleSearch,
}) => {
  const { cmsData } = config || {};

  const router = useRouter();
  const [formData, setFormData] = React.useState({ q: "" });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    toggleSearch?.();

    const query = formData.q.trim() ?? "";
    setFormData({ q: "" });

    const currentRoute = location?.pathname;
    const page = cmsData?.pages.find((x) => x.slug === currentRoute);
    const hasCaseList = page?.sections.some((x) => x.type === "CaseList");
    let defaultSearchPage = hasCaseList
      ? currentRoute
      : (cmsData?.defaultSearchPage ?? "/koeb");

    if (!defaultSearchPage.startsWith("/")) {
      defaultSearchPage = `/${defaultSearchPage}`;
    }

    if (formData.q.trim()) {
      router.push(`${defaultSearchPage}?q=${encodeURIComponent(query)}`);
    } else {
      router.push(defaultSearchPage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      style={
        {
          "--bg-color": config?.bgColor ?? "white",
        } as React.CSSProperties
      }
      className={twMerge("w-full p-2 flex", "bg-[var(--bg-color)]")}
    >
      <input
        type="text"
        id="q"
        name="q"
        style={
          {
            "--text-color":
              config?.input?.textColor ?? "var(--input-text-color)",
            "--bg-color": config?.input?.bgColor ?? "var(--input-bg-color)",
            "--border-color": config?.input?.borderColor ?? "",
            "--focus-border-color":
              config?.input?.focusBorderColor ?? "var(--input-text-color)",
            "--placeholder-text-color":
              config?.input?.placeholderTextColor ?? "text-gray-800",
          } as React.CSSProperties
        }
        className={twMerge(
          config?.input?.borderColor ? "border" : "",
          "p-2 w-full text-gray-800 focus:outline-none bg-[var(--bg-color)] text-[var(--text-color)] border-[var(--border-color)] focus:border-[var(--focus-border-color)] placeholder-[var(--placeholder-text-color)]",
          "placeholder:bg-green-[var(--placeholder-text-color)]"
        )}
        placeholder={
          config?.input?.placeholderText ??
          "Søg på sagsnr., by vej, postnr eller kommune"
        }
        value={formData.q}
        onChange={handleChange}
      />
      <RippleCtaButton
        type="submit"
        config={{
          bgColor: config?.button?.bgColor ?? "var(--search-button-bg-color)",
          textColor:
            config?.button?.textColor ?? "var(--search-button-text-color)",
          hoverBgColor:
            config?.button?.hoverBgColor ??
            "var(--search-button-hover-bg-color)",
          hoverTextColor:
            config?.button?.hoverTextColor ??
            "var(--search-button-hover-text-color)",
        }}
      >
        <span className="flex items-center gap-2">
          <FontAwesomeIcon className="size-[24px]" icon={faMagnifyingGlass} />
          {config?.button?.text || "Søg"}
        </span>
      </RippleCtaButton>
    </form>
  );
};

export default SearchBarComponent;
